import React, { useState, useContext } from 'react';
import { ThemeContext } from '../theme';
import { SearchNormal1, People } from 'iconsax-react';
import { Heart } from 'iconsax-react';
import useDragScroll from '../hooks/useDragScroll';

import InputField      from '../components/InputField';
import Button          from '../components/Button';
import FilterChip      from '../components/FilterChip';
import CommunityPostCard from '../components/CommunityPostCard';
import PollCard        from '../components/PollCard';
import TipCard         from '../components/TipCard';
import BlogCard        from '../components/BlogCard';

// ── Figma assets (~7-day URLs) ──────────────────────────────────────────────
const NOAH_AVATAR  = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1777995032/doctor_dp2_gehhrm.webp';
const JOANA_AVATAR = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776961467/doctor_dp_aoyun6.webp';
const STORY_IMG    = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960928/story_thumbnail_11_jcdkwc.webp';
const BLOG_IMG_1   = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960313/blogs_image_1_oo4px5.jpg';
const BLOG_IMG_2   = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960917/blogs_image_2_evcdm5.webp';
const BLOG_IMG_3   = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960919/blogs_image_3_ytndke.webp';

const SCALE = 0.75;
const sc = (n) => Math.round(n * SCALE);
const ROW_H = sc(220);

// ── Static data ──────────────────────────────────────────────────────────────
const FILTERS = ['All', 'Community', 'Blogs', 'Play packs', 'Bookmarked by you'];

const POLL_OPTIONS = [
  { label: 'Daily Bedtime Stories', percent: 60, active: true },
  { label: 'Everyday Playtime',     percent: 32, active: false },
];

const BLOGS = [
  {
    image: BLOG_IMG_1,
    title: 'Raising Kids Without Losing Yourself',
    author: 'Dr. John Doe',
    readTime: '3 min read',
    popularityChip: 'Read by 2.5K parents',
  },
  {
    image: BLOG_IMG_2,
    title: 'What to Say Instead of "Stop Crying"',
    author: 'Dr. John Doe',
    readTime: '3 min read',
  },
  {
    image: BLOG_IMG_3,
    title: 'The Power of Everyday Moments',
    author: 'Dr. John Doe',
    readTime: '3 min read',
  },
];

// ── Section heading ──────────────────────────────────────────────────────────
function SectionHeading({ children }) {
  const theme = useContext(ThemeContext);
  return (
    <h2
      className="font-bogart font-bold"
      style={{ margin: 0, fontSize: sc(24), lineHeight: `${sc(36)}px`, color: theme.text100 }}
    >
      {children}
    </h2>
  );
}

// ── Small library-story card (reused inline) ─────────────────────────────────
function StoryCard({ image, title, category }) {
  const theme = useContext(ThemeContext);
  const [liked, setLiked] = useState(false);
  return (
    <div
      style={{
        backgroundColor: theme.cardBg,
        border: `1px solid ${theme.border}`,
        borderRadius: sc(12),
        padding: sc(12),
        display: 'flex',
        flexDirection: 'column',
        gap: sc(12),
        width: sc(240),
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ flex: 1, borderRadius: sc(10), overflow: 'hidden' }}>
        <img src={image} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: sc(2) }}>
        <span className="font-satoshi font-bold" style={{ fontSize: sc(18), lineHeight: `${sc(24)}px`, color: theme.text100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </span>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="font-satoshi" style={{ flex: 1, fontSize: sc(14), fontWeight: 500, color: theme.text400 }}>{category}</span>
          <button onClick={() => setLiked(l => !l)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <Heart size={sc(24)} variant={liked ? 'Bold' : 'Linear'} color={liked ? theme.btnPrimaryBg : theme.text400} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main screen ──────────────────────────────────────────────────────────────
export default function CommunityScreen({ onShareThoughts }) {
  const theme = useContext(ThemeContext);
  const [search,     setSearch]     = useState('');
  const [activeFilter, setFilter]   = useState('All');
  const parentsCarousel = useDragScroll();
  const blogsCarousel   = useDragScroll();

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        backgroundColor: theme.cardBg100,
        padding: `${sc(20)}px ${sc(24)}px ${sc(32)}px`,
        display: 'flex',
        flexDirection: 'column',
        gap: sc(24),
      }}
    >
      {/* ── Top bar: search + CTA ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: sc(16) }}>
        <div style={{ flex: 1 }}>
          <InputField
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            rightIcon={<SearchNormal1 size={sc(24)} variant="Linear" color={theme.text400} />}
            scale={SCALE}
          />
        </div>
        <Button variant="primary" scale={SCALE} onClick={onShareThoughts} style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: sc(12) }}>
          <People size={sc(20)} variant="Linear" color="#FFFFFF" style={{ flexShrink: 0 }} />
          Share your thoughts
        </Button>
      </div>

      {/* ── Filter chips ── */}
      <div style={{ display: 'flex', gap: sc(12), alignItems: 'center', flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <FilterChip key={f} label={f} active={activeFilter === f} onClick={() => setFilter(f)} />
        ))}
      </div>

      {/* ── From Other Parents ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: sc(24) }}>
        <SectionHeading>From Other Parents</SectionHeading>
        <div
          {...parentsCarousel.props}
          style={{
            ...parentsCarousel.props.style,
            display: 'flex',
            gap: sc(12),
            alignItems: 'stretch',
            overflowX: 'auto',
            paddingBottom: sc(4),
            height: ROW_H,
          }}
        >
          <CommunityPostCard
            avatar={NOAH_AVATAR}
            name="Noah Malone"
            date="20th July 2025"
            text={'"We like to listen to a story before bedtime because it\u2019s a nice way to unwind"'}
            time="2:30PM"
          />
          <PollCard
            title="What helped you bond with your child the most?"
            votes="1,402 Votes"
            options={POLL_OPTIONS}
          />
          <StoryCard
            image={STORY_IMG}
            title="The Quiet Win"
            category="Value based"
          />
        </div>
      </div>

      {/* ── Tips & Blogs ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: sc(24) }}>
        <SectionHeading>Tips &amp; Blogs</SectionHeading>
        <div
          {...blogsCarousel.props}
          style={{
            ...blogsCarousel.props.style,
            display: 'flex',
            gap: sc(12),
            alignItems: 'stretch',
            overflowX: 'auto',
            paddingBottom: sc(4),
            height: ROW_H,
          }}
        >
          <TipCard
            avatar={JOANA_AVATAR}
            title="Expert Tip of the Day"
            author="Dr. Joana Wilson"
            text={'"Remember to celebrate small milestones in your child\u2019s growth"'}
            time="12:00PM"
            likeCount="1.5K"
          />
          {BLOGS.map((b, i) => (
            <BlogCard key={i} {...b} />
          ))}
        </div>
      </div>
    </div>
  );
}
